<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import {
  postItemLink,
  postCustomerLink,
  postInvoiceLink,
  postPaymentLink,
  getAssociationInfo,
  searchLytningItems,
  searchLytningCustomers,
  searchLytningInvoices,
  searchLytningPayments,
  type QboItemSearchResult,
  type QboCustomerSearchResult,
  type QboInvoiceSearchResult,
  type QboPaymentSearchResult,
  type LytningItemSearchResult,
  type LytningCustomerSearchResult,
  type LytningInvoiceSearchResult,
  type LytningPaymentSearchResult,
  type PrerequisiteDescriptor,
  type ItemAssociationRow,
  type CustomerAssociationRow,
  type InvoiceAssociationRow,
  type PaymentAssociationRow,
} from '@/api/lytAccountingClient'
import { useEntityAssociationDialogQboSearchQuery } from '@/composables/queries/useEntityAssociationDialogQboSearchQuery'
import { track } from '@/lib/usageTracker'
import { Alert, Button, AsyncAutocomplete } from '@/components/ui'

type EntityType = 'item' | 'customer' | 'invoice' | 'payment'
type Source = 'lytning' | 'qbo'

type LytningEntityProp = {
  lytsoftItemId?: string
  lytsoftCustomerId?: string
  lytsoftInvoiceId?: string
  lytsoftPaymentId?: string
  itemName?: string
  customerName?: string | null
  itemType?: string | null
  email?: string | null
  saleNumber?: number | null
  invoiceDate?: string | null
  paymentDate?: string | null
  totalAmount?: number | null
  paymentType?: string | null
  qboInvoiceId?: string | null
  qboPaymentId?: string | null
  syncedAt?: string | null
}

type QboEntityProp = QboItemSearchResult | QboCustomerSearchResult | QboInvoiceSearchResult | QboPaymentSearchResult

interface Props {
  open: boolean
  entityType: EntityType
  source: Source
  lytningEntity?: LytningEntityProp
  qboEntity?: QboEntityProp
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'associated', result: { success: boolean; message: string; alreadyAssociated?: boolean }): void
}>()

const selectedTargetId = ref('')
const selectedTarget = ref<unknown>(null)
const linking = ref(false)
const error = ref('')
const prerequisites = ref<PrerequisiteDescriptor[]>([])

const entityLabel = computed(() => {
  if (props.entityType === 'item') return 'Item'
  if (props.entityType === 'customer') return 'Customer'
  if (props.entityType === 'invoice') return 'Invoice'
  return 'Payment'
})

const sourceEntityName = computed(() => {
  if (props.source === 'lytning') {
    if (props.entityType === 'item') return props.lytningEntity?.itemName ?? ''
    if (props.entityType === 'customer') return props.lytningEntity?.customerName ?? ''
    if (props.entityType === 'payment') {
      return props.lytningEntity?.paymentType
        ?? props.lytningEntity?.customerName
        ?? props.lytningEntity?.lytsoftPaymentId
        ?? ''
    }
    const sale = props.lytningEntity?.saleNumber
    if (sale != null) return `Sale #${sale}`
    return props.lytningEntity?.customerName ?? props.lytningEntity?.lytsoftInvoiceId ?? ''
  }
  if (props.qboEntity && 'qboPaymentId' in props.qboEntity) {
    const pay = props.qboEntity as QboPaymentSearchResult
    return pay.docNumber || pay.vendorRef?.name || pay.qboPaymentId
  }
  if (props.qboEntity && 'name' in props.qboEntity) return props.qboEntity.name ?? ''
  return ''
})

const sourceEntityId = computed(() => {
  if (props.source === 'lytning') {
    return props.lytningEntity?.lytsoftItemId
      ?? props.lytningEntity?.lytsoftCustomerId
      ?? props.lytningEntity?.lytsoftInvoiceId
      ?? props.lytningEntity?.lytsoftPaymentId
      ?? ''
  }
  if (props.qboEntity && 'qboItemId' in props.qboEntity) return props.qboEntity.qboItemId
  if (props.qboEntity && 'qboCustomerId' in props.qboEntity) return props.qboEntity.qboCustomerId
  if (props.qboEntity && 'qboInvoiceId' in props.qboEntity) return props.qboEntity.qboInvoiceId
  if (props.qboEntity && 'qboPaymentId' in props.qboEntity) return props.qboEntity.qboPaymentId
  return ''
})

const sourceEntityType = computed(() => {
  if (props.source === 'lytning' && props.lytningEntity) {
    if (props.entityType === 'item') return props.lytningEntity.itemType ?? '—'
    if (props.entityType === 'customer') return props.lytningEntity.email ?? '—'
    if (props.entityType === 'payment') {
      if (props.lytningEntity.paymentDate) return props.lytningEntity.paymentDate
      if (props.lytningEntity.totalAmount != null) return String(props.lytningEntity.totalAmount)
      return '—'
    }
    if (props.lytningEntity.invoiceDate) return props.lytningEntity.invoiceDate
    if (props.lytningEntity.totalAmount != null) return String(props.lytningEntity.totalAmount)
    return '—'
  }
  if (props.qboEntity) {
    if ('type' in props.qboEntity) return props.qboEntity.type ?? '—'
    if ('email' in props.qboEntity) return props.qboEntity.email ?? '—'
    if ('qboPaymentId' in props.qboEntity) {
      const pay = props.qboEntity as QboPaymentSearchResult
      return pay.vendorRef?.name || pay.entity || pay.txnDate || '—'
    }
    if ('txnDate' in props.qboEntity) {
      const inv = props.qboEntity as QboInvoiceSearchResult
      return inv.customerRef?.name || inv.txnDate || '—'
    }
  }
  return '—'
})

const targetSystem = computed(() => props.source === 'lytning' ? 'QBO' : 'Lytning')

const existingAssociation = computed(() => {
  if (!props.lytningEntity) return null
  if (props.entityType === 'invoice') {
    // getAssociationInfo currently types item|customer; invoice uses qboInvoiceId when present
    const inv = props.lytningEntity as InvoiceAssociationRow
    if (inv.qboInvoiceId) {
      return { provider: 'QBO', providerId: inv.qboInvoiceId, syncedAt: inv.syncedAt ?? null }
    }
    return null
  }
  if (props.entityType === 'payment') {
    const pay = props.lytningEntity as PaymentAssociationRow
    if (pay.qboPaymentId) {
      return { provider: 'QBO', providerId: pay.qboPaymentId, syncedAt: pay.syncedAt ?? null }
    }
    return null
  }
  return getAssociationInfo(props.lytningEntity as ItemAssociationRow | CustomerAssociationRow)
})

watch(() => props.open, (isOpen) => {
  if (isOpen) {
    selectedTargetId.value = ''
    selectedTarget.value = null
    error.value = ''
    prerequisites.value = []
  }
})

function resolveQboId(): string {
  if (props.source === 'qbo') return sourceEntityId.value
  return selectedTargetId.value
}

function resolveLytId(): string {
  if (props.source === 'lytning') return sourceEntityId.value
  return selectedTargetId.value
}

async function handleLink() {
  track('btn:ConfirmLink', 'click')
  error.value = ''
  prerequisites.value = []
  linking.value = true

  try {
    const qboId = resolveQboId()
    const lytId = resolveLytId()

    if (!qboId || !lytId) {
      error.value = 'Both QBO and Lytning identifiers are required to create a link.'
      return
    }

    let response: {
      row: unknown
      alreadyAssociated: boolean
      error?: string
      prerequisites?: PrerequisiteDescriptor[]
    }

    if (props.entityType === 'item') {
      response = await postItemLink(qboId, lytId)
    } else if (props.entityType === 'customer') {
      response = await postCustomerLink(qboId, lytId)
    } else if (props.entityType === 'payment') {
      response = await postPaymentLink(qboId, lytId)
    } else {
      response = await postInvoiceLink(qboId, lytId)
    }

    if (response.error) {
      error.value = response.error
      prerequisites.value = response.prerequisites ?? []
      return
    }

    // Success or exact-idempotent (alreadyAssociated without error) closes the dialog
    const message = response.alreadyAssociated
      ? 'Already associated'
      : `${entityLabel.value} linked successfully`
    emit('associated', {
      success: true,
      message,
      alreadyAssociated: response.alreadyAssociated,
    })
    emit('update:open', false)
  } catch (e) {
    error.value = (e as Error)?.message || 'Association failed'
  } finally {
    linking.value = false
  }
}

function closeDialog() {
  emit('update:open', false)
}

// --- AsyncAutocomplete helpers (SearchResult union) ---
type SearchResult =
  | QboItemSearchResult
  | QboCustomerSearchResult
  | QboInvoiceSearchResult
  | QboPaymentSearchResult
  | LytningItemSearchResult
  | LytningCustomerSearchResult
  | LytningInvoiceSearchResult
  | LytningPaymentSearchResult


function handleSelect(result: SearchResult) {
  selectedTarget.value = result
}

// QBO target search (Lytning-origin) goes through a scoped Colada query boundary.
// Lytning target search (QBO-origin) stays on typed client helpers for now.
const qboSearch = useEntityAssociationDialogQboSearchQuery({
  entityType: () => props.entityType,
})

const searchFn = (query: string): Promise<SearchResult[]> => {
  if (props.source === 'lytning') {
    return qboSearch.search(query) as Promise<SearchResult[]>
  }
  if (props.entityType === 'item') return searchLytningItems(query) as Promise<SearchResult[]>
  if (props.entityType === 'customer') return searchLytningCustomers(query) as Promise<SearchResult[]>
  if (props.entityType === 'payment') return searchLytningPayments(query) as Promise<SearchResult[]>
  return searchLytningInvoices(query) as Promise<SearchResult[]>
}

function getItemId(item: SearchResult): string {
  if ('qboItemId' in item) return item.qboItemId
  if ('qboCustomerId' in item) return item.qboCustomerId
  if ('qboInvoiceId' in item) return item.qboInvoiceId
  if ('qboPaymentId' in item) return item.qboPaymentId
  if ('lytsoftItemId' in item) return item.lytsoftItemId
  if ('lytsoftCustomerId' in item) return item.lytsoftCustomerId
  if ('lytsoftInvoiceId' in item) return item.lytsoftInvoiceId
  if ('lytsoftPaymentId' in item) return item.lytsoftPaymentId
  return ''
}

function getItemLabel(item: SearchResult): string {
  if ('qboPaymentId' in item) return item.docNumber || item.qboPaymentId
  if ('lytsoftPaymentId' in item) return item.name
  if ('name' in item && item.name) return item.name
  if ('docNumber' in item) return item.docNumber || item.qboInvoiceId
  return getItemId(item)
}

function formatResult(item: SearchResult) {
  if ('qboItemId' in item) {
    return { label: item.name, subtext: item.type }
  }
  if ('lytsoftItemId' in item) {
    return { label: item.name, subtext: item.type }
  }
  if ('qboCustomerId' in item) {
    return { label: item.name, subtext: item.email ?? undefined }
  }
  if ('lytsoftCustomerId' in item) {
    return { label: item.name, subtext: item.email ?? undefined }
  }
  if ('qboPaymentId' in item) {
    const pay = item as QboPaymentSearchResult
    return {
      label: pay.docNumber || pay.qboPaymentId,
      subtext: pay.vendorRef?.name || pay.entity,
    }
  }
  if ('lytsoftPaymentId' in item) {
    const pay = item as LytningPaymentSearchResult
    return {
      label: pay.name,
      subtext: pay.customerName ?? pay.paymentType ?? pay.paymentDate ?? undefined,
    }
  }
  if ('qboInvoiceId' in item) {
    const inv = item as QboInvoiceSearchResult
    return {
      label: inv.docNumber || inv.qboInvoiceId,
      subtext: inv.customerRef?.name || inv.txnDate,
    }
  }
  const inv = item as LytningInvoiceSearchResult
  return {
    label: inv.name,
    subtext: inv.customerName ?? (inv.saleNumber != null ? `Sale #${inv.saleNumber}` : undefined),
  }
}

// unknown-typed wrappers for AsyncAutocomplete props
function getItemIdUnknown(item: unknown): string { return getItemId(item as SearchResult) }
function getItemLabelUnknown(item: unknown): string { return getItemLabel(item as SearchResult) }
function formatResultUnknown(item: unknown) { return formatResult(item as SearchResult) }
function handleSelectUnknown(item: unknown) { handleSelect(item as SearchResult) }
</script>
<template>
  <Teleport to="body">
    <div v-if="open" class="dialog-overlay" @click.self="$emit('update:open', false)">
      <div class="dialog-content">
        <div class="dialog-header">
          <h3>Link {{ entityLabel }}</h3>
          <button class="dialog-close" @click="closeDialog">×</button>
        </div>

        <!-- Source entity info -->
        <div class="dialog-section source-info">
          <div class="entity-detail">
            <span class="detail-label">{{ source === 'lytning' ? 'Lytning' : 'QBO' }} {{ entityType }}:</span>
            <span class="detail-value">{{ sourceEntityName }}</span>
            <span class="detail-id">({{ sourceEntityType }})</span>
          </div>
          <div class="entity-detail">
            <span class="detail-label">ID:</span>
            <span class="detail-id">{{ sourceEntityId }}</span>
          </div>
        </div>

        <!-- Existing association warning -->
        <Alert v-if="existingAssociation" variant="warning" title="Already linked">
          This {{ entityType }} is already linked to {{ existingAssociation.provider }} ID {{ existingAssociation.providerId }}
          <span v-if="existingAssociation.syncedAt"> (synced: {{ new Date(existingAssociation.syncedAt).toLocaleDateString() }})</span>
        </Alert>

        <!-- Target search -->
        <div class="dialog-section search-section">
          <label class="search-label">Search {{ targetSystem }} {{ entityType }}</label>
          <AsyncAutocomplete
            v-model="selectedTargetId"
            :fetchResults="searchFn as (query: string) => Promise<unknown[]>"
            :getItemId="getItemIdUnknown"
            :getItemLabel="getItemLabelUnknown"
            :formatResult="formatResultUnknown"
            :placeholder="`Search ${entityType} by name...`"
            @select="handleSelectUnknown"
          />
        </div>

        <!-- Error / conflict / prerequisites -->
        <Alert v-if="error" variant="error" title="Association failed">
          {{ error }}
          <ul v-if="prerequisites.length" class="prereq-list">
            <li v-for="p in prerequisites" :key="`${p.type}-${p.lytsoftId}`">
              {{ p.type }}: {{ p.lytsoftId }}
              <span v-if="p.deepLink" class="prereq-link">({{ p.deepLink }})</span>
            </li>
          </ul>
        </Alert>

        <!-- Actions -->
        <div class="dialog-actions">
          <Button variant="secondary" @click="closeDialog">Cancel</Button>
          <Button
            :loading="linking"
            :disabled="!selectedTargetId"
            @click="handleLink"
          >
            Link
          </Button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  padding: 1rem;
}

.dialog-content {
  width: min(32rem, 100%);
  background: var(--surface, #fff);
  border-radius: 0.75rem;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.18);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.dialog-header h3 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
}

.dialog-close {
  border: none;
  background: transparent;
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
  color: #64748b;
}

.dialog-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.source-info {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.75rem;
}

.entity-detail {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.5rem;
  align-items: baseline;
  font-size: 0.9rem;
}

.detail-label {
  color: #64748b;
  font-weight: 500;
}

.detail-value {
  color: #0f172a;
  font-weight: 600;
}

.detail-id {
  color: #64748b;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.8rem;
}

.search-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: #334155;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.prereq-list {
  margin: 0.5rem 0 0;
  padding-left: 1.1rem;
}

.prereq-link {
  color: #64748b;
  font-size: 0.85em;
}
</style>
